import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { Card, Form, Input, Button, Space, Spin, message, Popconfirm } from 'antd';
import { ArrowLeftOutlined, SaveOutlined, DeleteOutlined } from '@ant-design/icons';
import { getGenre } from '../../graphql/queries';
import { updateGenre, deleteGenre } from '../../graphql/mutation';

const { TextArea } = Input;

const GenreDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const { loading } = useQuery(getGenre, {
    variables: { id },
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.getGenre) {
        form.setFieldsValue({
          name: data.getGenre.name,
          icon: data.getGenre.icon,
          prompt: data.getGenre.prompt,
        });
      }
    }
  });

  const [update, { loading: updating }] = useMutation(updateGenre, {
    onCompleted: () => {
      message.success('Genre updated');
      navigate('/genres');
    },
    onError: (error) => {
      message.error(`Failed to update: ${error.message}`);
    }
  });

  const [deleteGenreMutation, { loading: deleting }] = useMutation(deleteGenre, {
    onCompleted: () => {
      message.success('Genre deleted');
      navigate('/genres');
    },
    onError: (error) => {
      message.error(`Failed to delete: ${error.message}`);
    }
  });

  const handleSubmit = (values) => {
    update({
      variables: {
        id,
        name: values.name,
        icon: values.icon,
        prompt: values.prompt,
      }
    });
  };

  const handleDelete = () => {
    deleteGenreMutation({ variables: { id } });
  };

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/genres')}>
              Back
            </Button>
            <span>Edit Genre</span>
          </Space>
        }
        extra={
          <Popconfirm
            title="Delete this genre?"
            onConfirm={handleDelete}
            okText="Yes"
            cancelText="No"
          >
            <Button danger icon={<DeleteOutlined />} loading={deleting}>
              Delete
            </Button>
          </Popconfirm>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          style={{ maxWidth: 600 }}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Please enter a name' }]}
          >
            <Input placeholder="e.g., Pop, Rock, Jazz" />
          </Form.Item>

          <Form.Item
            label="Icon"
            name="icon"
            rules={[{ required: true, message: 'Please enter an icon' }]}
          >
            <Input placeholder="e.g., emoji or icon code" />
          </Form.Item>

          <Form.Item
            label="Prompt"
            name="prompt"
            rules={[{ required: true, message: 'Please enter a prompt' }]}
          >
            <TextArea rows={4} placeholder="Genre prompt text..." />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={updating}>
              Save Changes
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default GenreDetail;
